// [S-802] Where a character of the FLATTENED template sits in the HTML.
//
// A tag set's spans are offsets into `templateTextForTagging(html)`. That is the
// authoritative answer to "where is this slot" — it was measured once, when the
// template was tagged, and every slot in an approved set sits exactly on a gap
// in that text (verified on tpl-a7fc6bc79eae0c1a: 30 of 30). Filling a draft
// then only needs those offsets carried back into the HTML.
//
// Before this, the fill went looking for each slot instead: match the words
// before it, else match its own text, walking a cursor forward. Every part of
// that was a guess, and it failed the way guesses do. A performer agreement
// says "PERFORMER AGREEMENT" twice — once at the top, once in the exhibit — so
// `company`'s context matched the LATE one, the cursor jumped to block 164, and
// `artist`, `role` and two more were left behind it and refused. The draft
// opened with the agreement date in the title's gap and the company's name
// written where the date belongs.
//
// Alignment, not reimplementation. `htmlToText` is a chain of replaces that
// inserts text (list numbers) and collapses whitespace; writing an
// offset-tracking twin of it would be a second copy to disagree with the first.
// Instead the two strings we already have are walked together. What the map
// cannot place, it says so about — and a caller that cannot place a slot leaves
// the template's own text alone.
/** Characters the flattening may add or drop with no counterpart in the HTML:
 *  a list number it writes in, whitespace it collapses. */
const SKIPPABLE = /[\s]/;
/** A tag set is built over `normalizeQuotes(...)`, so the flattened text holds
 *  straight quotes where the HTML has the typographic ones the editor inserted.
 *  Comparing them raw left eight slots on this corpus unable to vouch for
 *  themselves. */
const fold = (c) => c === "“" || c === "”" ? '"' : c === "‘" || c === "’" ? "'" : c;
/**
 * For each index of `flat`, the index in `html` the character came from, or -1
 * when the flattening invented it (a list number) — so a caller can refuse
 * rather than write into the wrong place.
 *
 * Greedy and linear: the flattened text is a subsequence of the HTML's text
 * content, so a pointer into the HTML only ever moves forward, past tags and
 * entities, until the characters agree.
 */
export function flattenedToHtmlOffsets(html, flat) {
    const at = new Array(flat.length).fill(-1);
    let h = 0;
    const skipMarkup = () => {
        for (;;) {
            if (html[h] === "<") {
                const close = html.indexOf(">", h);
                h = close === -1 ? html.length : close + 1;
                continue;
            }
            return;
        }
    };
    // Every decision here is LOCAL: the next character of the HTML either is the
    // one the flattened text wants, or the flattened text invented it. Searching
    // ahead for a match is what a guess looks like — a written-in "1." would find
    // the full stop of "per week." further down and drag the pointer past a gap,
    // which is how the old fill put the date in the title.
    for (let f = 0; f < flat.length; f += 1) {
        const want = flat[f];
        skipMarkup();
        if (h >= html.length)
            break;
        const ch = html[h];
        // An entity stands for ONE character and must be read before the plain
        // comparison: "&amp;" starts with the "&" the flattened text wants, and
        // matching it naively leaves "amp;" behind to be walked as if it were text.
        if (ch === "&") {
            const semi = html.indexOf(";", h);
            const name = semi > h && semi - h <= 8 ? html.slice(h + 1, semi).toLowerCase() : null;
            const decoded = name === null ? undefined : ENTITY[name];
            if (decoded !== undefined) {
                if (fold(decoded) === fold(want)) {
                    at[f] = h;
                    h = semi + 1;
                    continue;
                }
                // A real character the flattened text has already dealt with.
                h = semi + 1;
                f -= 1;
                continue;
            }
        }
        if (fold(ch) === fold(want)) {
            at[f] = h;
            h += 1;
            continue;
        }
        // Whitespace the flattening collapsed: drop it from the HTML side and ask
        // again for the same flattened character.
        if (SKIPPABLE.test(ch) && !SKIPPABLE.test(want)) {
            h += 1;
            f -= 1;
            continue;
        }
        // Whitespace the flattening ADDED (a newline for </p>) and any character it
        // wrote in itself (a list number) have no home. Leave the pointer where it
        // is: the next flattened character is still expected here.
        if (SKIPPABLE.test(want))
            continue;
        if (/[\dA-Za-z.()]/.test(want))
            continue;
        // Anything else is a divergence, not an insertion. Step the HTML on once so
        // the walk cannot stall, and leave this character unmapped.
        h += 1;
    }
    return at;
}
const ENTITY = {
    amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", "#39": "'", nbsp: " ", "#160": " ",
};
/** The HTML span a flattened `[from, to)` occupies, or null when the map cannot
 *  vouch for it: an unmapped edge, or an HTML slice whose own text does not read
 *  back as the flattened one. Verified rather than assumed, because the cost of
 *  being wrong is a value written into the wrong clause. */
export function htmlSpanOf(at, from, to) {
    if (to <= from || to > at.length)
        return null;
    const first = at[from];
    if (first === undefined || first < 0)
        return null;
    let last = -1;
    for (let i = to - 1; i >= from; i -= 1) {
        const h = at[i];
        if (h !== undefined && h >= 0) {
            last = h;
            break;
        }
    }
    if (last < first)
        return null;
    return { from: first, to: last + 1 };
}
//# sourceMappingURL=templateTextMap.js.map